import React, { useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Collapse,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Avatar,
  Chip,
} from "@mui/material";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import LocationCityRoundedIcon from "@mui/icons-material/LocationCityRounded";
import AssignmentRoundedIcon from "@mui/icons-material/AssignmentRounded";
import CategoryRoundedIcon from "@mui/icons-material/CategoryRounded";
import EventRoundedIcon from "@mui/icons-material/EventRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import PersonAddAltRoundedIcon from "@mui/icons-material/PersonAddAltRounded";
import PeopleRoundedIcon from "@mui/icons-material/PeopleRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import PhotoLibraryRoundedIcon from "@mui/icons-material/PhotoLibraryRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import PlaceRoundedIcon from "@mui/icons-material/PlaceRounded";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
import MapRoundedIcon from "@mui/icons-material/MapRounded";
import CardMembershipRoundedIcon from "@mui/icons-material/CardMembershipRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import BugReportRoundedIcon from "@mui/icons-material/BugReportRounded";
import { VM_Colors } from "../../Styles/VMStyles";
import {
  ROUTE_LOGIN,
  ROUTE_SUPER_ADMIN,
  ROUTE_SUPER_ADMIN_VENUES,
  ROUTE_SUPER_ADMIN_USERS,
  ROUTE_SUPER_ADMIN_ENQUIRIES,
  ROUTE_SUPER_ADMIN_EVENT_TYPES,
  ROUTE_SUPER_ADMIN_EVENTS,
  ROUTE_SUPER_ADMIN_VISITOR_LOGS,
  ROUTE_SUPER_ADMIN_EVENT_PHOTOS,
  ROUTE_SUPER_ADMIN_VENDORS,
  ROUTE_SUPER_ADMIN_ADD_ADMIN,
  ROUTE_SUPER_ADMIN_DELETED_ACCOUNTS,
  ROUTE_SUPER_ADMIN_COUNTRY_MASTER,
  ROUTE_SUPER_ADMIN_STATE_MASTER,
  ROUTE_SUPER_ADMIN_CITY_MASTER,
  ROUTE_SUPER_ADMIN_SUBSCRIPTIONS,
  ROUTE_SUPER_ADMIN_SUBSCRIPTION_PLANS,
  ROUTE_SUPER_ADMIN_ERROR_LOGS,
} from "../../Util/VMConstants";
import { clearAppData } from "../../Util/FCM/clearAppData";
import StorageFactory from "../../Util/StorageFactory";
import { SESSION_LOGIN_NAME_KEY, SESSION_USER_NAME_KEY } from "../../Util/VMConstants";
import { useAdminPanel } from "./hooks/useAdminPanel";

const drawerWidth = 268;

const allMenuItems = [
  { label: "Dashboard", to: `/${ROUTE_SUPER_ADMIN}`, end: true, icon: <DashboardRoundedIcon /> },
  { label: "Venues", to: `/${ROUTE_SUPER_ADMIN_VENUES}`, icon: <LocationCityRoundedIcon /> },
  { label: "Users", to: `/${ROUTE_SUPER_ADMIN_USERS}`, icon: <PeopleRoundedIcon /> },
  { label: "Enquiries", to: `/${ROUTE_SUPER_ADMIN_ENQUIRIES}`, icon: <AssignmentRoundedIcon /> },
  { label: "Events", to: `/${ROUTE_SUPER_ADMIN_EVENTS}`, icon: <EventRoundedIcon /> },
  { label: "Event Types", to: `/${ROUTE_SUPER_ADMIN_EVENT_TYPES}`, icon: <CategoryRoundedIcon /> },
  { label: "Visitor Logs", to: `/${ROUTE_SUPER_ADMIN_VISITOR_LOGS}`, icon: <MenuBookRoundedIcon /> },
  { label: "Event Photos", to: `/${ROUTE_SUPER_ADMIN_EVENT_PHOTOS}`, icon: <PhotoLibraryRoundedIcon /> },
  { label: "Event Vendors", to: `/${ROUTE_SUPER_ADMIN_VENDORS}`, icon: <StorefrontRoundedIcon /> },
  { label: "Subscription", to: `/${ROUTE_SUPER_ADMIN_SUBSCRIPTIONS}`, icon: <PaymentsRoundedIcon />, superAdminOnly: true },
  { label: "Add Admin", to: `/${ROUTE_SUPER_ADMIN_ADD_ADMIN}`, icon: <PersonAddAltRoundedIcon />, superAdminOnly: true },
  { label: "Deleted Accounts", to: `/${ROUTE_SUPER_ADMIN_DELETED_ACCOUNTS}`, icon: <DeleteOutlineRoundedIcon /> },
];

const settingsSubItems = [
  { label: "Country master", to: `/${ROUTE_SUPER_ADMIN_COUNTRY_MASTER}`, icon: <PublicRoundedIcon fontSize="small" /> },
  { label: "State master", to: `/${ROUTE_SUPER_ADMIN_STATE_MASTER}`, icon: <MapRoundedIcon fontSize="small" /> },
  { label: "City master", to: `/${ROUTE_SUPER_ADMIN_CITY_MASTER}`, icon: <PlaceRoundedIcon fontSize="small" /> },
  { label: "Subscription", to: `/${ROUTE_SUPER_ADMIN_SUBSCRIPTION_PLANS}`, icon: <CardMembershipRoundedIcon fontSize="small" /> },
  { label: "Error Logs", to: `/${ROUTE_SUPER_ADMIN_ERROR_LOGS}`, icon: <BugReportRoundedIcon fontSize="small" /> },
];

const navItemSx = {
  borderRadius: 1,
  mb: 0.5,
  "&.active": {
    bgcolor: "primary.main",
    color: "primary.contrastText",
    "& .MuiListItemIcon-root": { color: "inherit" },
  },
};

export default function VMSuperAdmin() {
  const navigate = useNavigate();
  const location = useLocation();
  const session = useMemo(() => new StorageFactory("session"), []);
  const displayName = session.get(SESSION_USER_NAME_KEY) || "";
  const loginName = session.get(SESSION_LOGIN_NAME_KEY) || "";
  const { adminType, adminScope, canManageAdmins } = useAdminPanel();

  const settingsActive = settingsSubItems.some((item) => location.pathname === item.to);
  const [settingsOpen, setSettingsOpen] = useState(settingsActive);

  useEffect(() => {
    if (settingsActive) setSettingsOpen(true);
  }, [settingsActive]);

  const menuItems = useMemo(
    () => allMenuItems.filter((item) => !item.superAdminOnly || canManageAdmins),
    [canManageAdmins]
  );

  const handleLogout = async () => {
    try {
      await clearAppData();
    } finally {
      navigate(`/${ROUTE_LOGIN}`, { replace: true });
    }
  };

  const scopeLabel =
    adminScope?.city || adminScope?.state || adminScope?.country
      ? [adminScope.city, adminScope.state, adminScope.country].filter(Boolean).join(", ")
      : null;

  return (
    <Box sx={{ display: "flex", minHeight: "calc(100vh - 48px)", ml: -3, mr: -3, mt: -3, mb: -3 }}>
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            borderRight: "1px solid #e3e8ef",
            bgcolor: "#fff",
            height: "calc(100vh - 48px)",
            overflow: "hidden",
          },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
          <Toolbar sx={{ flexShrink: 0, flexDirection: "column", alignItems: "flex-start", py: 2, gap: 1 }}>
            <Avatar sx={{ bgcolor: VM_Colors.primary, width: 48, height: 48, fontWeight: 700, fontSize: "1.1rem" }}>VM</Avatar>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: VM_Colors.primary }}>
              {displayName || "Venue Manager Admin"}
            </Typography>
            {loginName ? (
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>
                {loginName}
              </Typography>
            ) : null}
            <Chip size="small" label={adminType} color={adminType === "subAdmin" ? "secondary" : "primary"} />
            {scopeLabel ? (
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>
                Scope: {scopeLabel}
              </Typography>
            ) : null}
          </Toolbar>
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              overflowX: "hidden",
              px: 1,
            }}
          >
            <List disablePadding>
              {menuItems.map((item) => (
                <ListItemButton
                  key={item.to}
                  component={NavLink}
                  to={item.to}
                  end={item.end}
                  sx={navItemSx}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: "primary.main" }}>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: "0.95rem" }} />
                </ListItemButton>
              ))}

              <ListItemButton
                onClick={() => setSettingsOpen((open) => !open)}
                sx={{
                  ...navItemSx,
                  ...(settingsActive ? { bgcolor: "action.selected" } : {}),
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: "primary.main" }}>
                  <SettingsRoundedIcon />
                </ListItemIcon>
                <ListItemText primary="Settings" primaryTypographyProps={{ fontSize: "0.95rem" }} />
                {settingsOpen ? <ExpandLess color="primary" /> : <ExpandMore color="primary" />}
              </ListItemButton>

              <Collapse in={settingsOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {settingsSubItems.map((subItem) => (
                    <ListItemButton
                      key={subItem.to}
                      component={NavLink}
                      to={subItem.to}
                      sx={{
                        ...navItemSx,
                        pl: 4,
                        ml: 1,
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 32, color: "primary.main" }}>{subItem.icon}</ListItemIcon>
                      <ListItemText primary={subItem.label} primaryTypographyProps={{ fontSize: "0.9rem" }} />
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            </List>
          </Box>
          <List sx={{ flexShrink: 0, px: 1, pb: 2, pt: 1, borderTop: "1px solid #e3e8ef" }}>
            <ListItemButton onClick={handleLogout} sx={{ borderRadius: 1, color: "error.main" }}>
              <ListItemIcon sx={{ minWidth: 40, color: "error.main" }}>
                <LogoutRoundedIcon />
              </ListItemIcon>
              <ListItemText primary="Logout" primaryTypographyProps={{ fontSize: "0.95rem", fontWeight: 600 }} />
            </ListItemButton>
          </List>
        </Box>
      </Drawer>
      <Box component="main" sx={{ flexGrow: 1,m:2,p: 3, bgcolor: VM_Colors.white1, minWidth: 0 }}>
        <Outlet />
      </Box>
    </Box>
  );
}
